import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import strings from "translate/language";
import { getFollowUpDetail } from "reduxStore/action/followUpActions";
import { handleDetailResponse, isJSON, seo } from "utils/utils";
import { genderCheck } from "utils/constants";
import Breadcrumb from "component/common/breadcrumb";
import "./FollowupListing.css";
import "component/common/JustoDetailFooter.css";

const displayValue = (value) => {
  if (value == null || value === "" || value === "NA") {
    return strings.notavailable;
  }
  return value;
};

const isEmpty = (value) =>
  value == null || value === "" || value === "NA";

const toTitleCase = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const Fact = ({ label, value }) => {
  const empty = isEmpty(value);
  return (
    <div className="justo-followup-detail__fact">
      <dt>{label}</dt>
      <dd className={empty ? "is-muted" : undefined}>{displayValue(value)}</dd>
    </div>
  );
};

const FactSection = ({ title, rows }) => (
  <section className="justo-followup-detail__section">
    <h2 className="justo-followup-detail__section-title">
      {toTitleCase(title)}
    </h2>
    <dl className="justo-followup-detail__facts">
      {rows.map((row) => (
        <Fact key={row.label} label={row.label} value={row.value} />
      ))}
    </dl>
  </section>
);

const ViewFollowUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const { _id = "" } = params || {};
  const [viewData, setViewData] = useState({});

  const followupDetailSelector = useSelector((state) => state.followUp) || {};

  useEffect(() => {
    if (followupDetailSelector.detail) {
      const { response } = followupDetailSelector;
      if (response?.status === 200) {
        const result = {
          ...handleDetailResponse(response.data),
          ...handleDetailResponse(response.data)?.customer,
        };
        const typeofaddress = typeof result?.office_address;
        let office_address = {};
        if (typeofaddress === "string" || typeofaddress === "object") {
          office_address =
            typeofaddress === "object"
              ? result?.office_address
              : isJSON(result?.office_address)
                ? JSON.parse(result?.office_address)
                : null;
        }
        setViewData({ ...result, office_address });
      }
    }
  }, [followupDetailSelector]);

  useEffect(() => {
    dispatch(getFollowUpDetail({ followup_id: _id }));
  }, [_id, dispatch]);

  useEffect(() => {
    seo({
      title: "Followup Details",
      metaDescription: "Followup details",
      canonicalLink: window.location.href,
    });
  }, []);

  const visitorName =
    `${viewData?.customer?.first_name || ""} ${
      viewData?.customer?.last_name || ""
    }`.trim() ||
    viewData?.customer_first_name ||
    "";

  const appointmentId = viewData?.appointment_id;

  const budgetValue = viewData?.budget_amount
    ? viewData.budget_amount
    : viewData?.min_budget && viewData?.max_budget
      ? `${viewData.min_budget}${viewData.min_budget_type} - ${viewData.max_budget}${viewData.max_budget_type}`
      : "";

  const lastInteracted = viewData?.followup_date
    ? moment(viewData.followup_date).format("DD-MM-YYYY")
    : "";

  const officeAddress =
    typeof viewData?.customer?.office_address === "string"
      ? viewData.customer.office_address
      : viewData?.office_address
        ? [
            viewData.office_address?.house_no,
            viewData.office_address?.area,
            viewData.office_address?.landmark,
            viewData.office_address?.location,
            viewData.office_address?.pincode,
          ]
            .filter(Boolean)
            .join(", ")
        : "";

  const followupRows = [
    { label: strings.lastInteracted, value: lastInteracted },
    { label: strings.source, value: viewData?.created_name },
    {
      label: strings.status,
      value: viewData?.followup_status_title || viewData?.followup_status,
    },
    {
      label: strings.visitingScore,
      value: viewData?.visit_score ?? viewData?.lead_score,
    },
  ];

  const propertyRows = [
    { label: strings.configuration, value: viewData?.configuration },
    { label: strings.Area, value: viewData?.areain_sqlft },
    { label: strings.budget, value: budgetValue },
    { label: strings.natureOfFunding, value: viewData?.funding_type },
    { label: strings.purpose, value: viewData?.purpose },
  ];

  const customerRows = [
    { label: strings.visitorName, value: visitorName },
    { label: strings.mobileNo, value: viewData?.customer?.mobile },
    { label: strings.location, value: viewData?.customer?.location },
    { label: strings.age, value: viewData?.customer?.age ?? viewData?.age },
    {
      label: strings.gender,
      value: genderCheck[viewData?.customer?.gender],
    },
    {
      label: strings.nameOfOccupation,
      value: viewData?.customer?.occupation,
    },
    {
      label: strings.companyName,
      value: viewData?.customer?.coumpany_name,
    },
    {
      label: strings.designation,
      value: viewData?.customer?.desigantion,
    },
    { label: strings.officeAddress, value: officeAddress },
  ];

  return (
    <div className="page-body justo-followup-page">
      <Breadcrumb
        title={strings.viewfollowupDetails}
        url={"/followup"}
        parentListing={strings.followupList}
        titleactive={strings.viewfollowupdetails}
        isBackBtn={true}
      />

      <div className="container-fluid justo-followup">
        <div className="justo-followup__card justo-followup-detail">
          <div className="justo-followup-detail__hero">
            <div className="justo-followup-detail__hero-main">
              <h1 className="justo-followup-detail__property">
                {displayValue(viewData?.property_title)}
              </h1>
              <p className="justo-followup-detail__meta">
                {visitorName ? (
                  <span>
                    {strings.visitorName}: <strong>{visitorName}</strong>
                  </span>
                ) : null}
                {viewData?.created_name ? (
                  <span>
                    {strings.source}:{" "}
                    <strong>{viewData.created_name}</strong>
                  </span>
                ) : null}
                {viewData?.customer?.mobile ? (
                  <span>
                    {strings.mobileNo}:{" "}
                    <strong>{viewData.customer.mobile}</strong>
                  </span>
                ) : null}
              </p>
            </div>
            <div className="justo-followup-detail__hero-actions">
              {viewData?.followup_status_title || viewData?.followup_status ? (
                <span className="justo-followup__status">
                  <i className="fa fa-phone" aria-hidden="true" />
                  <span className="justo-followup__status-label">
                    {viewData?.followup_status_title ||
                      viewData?.followup_status}
                  </span>
                </span>
              ) : null}
            </div>
          </div>

          <div className="justo-followup-detail__content">
            <div className="justo-followup-detail__grid">
              <FactSection
                title={strings.followupDetails}
                rows={followupRows}
              />
              <FactSection
                title={strings.propertyRequired}
                rows={propertyRows}
              />
              <FactSection
                title={strings.customerDetails}
                rows={customerRows}
              />
            </div>
          </div>

          <div className="justo-detail-footer">
            {appointmentId ? (
              <button
                type="button"
                className="justo-detail-footer__btn justo-detail-footer__btn--soft"
                onClick={() => navigate(`/revisit/${appointmentId}`)}
              >
                <i className="fa fa-phone" aria-hidden="true" />
                {strings.revisit}
              </button>
            ) : viewData?.lead_id ? (
              <button
                type="button"
                className="justo-detail-footer__btn justo-detail-footer__btn--soft"
                onClick={() => navigate(`/allfollowup/${viewData.lead_id}`)}
              >
                <i className="fa fa-phone" aria-hidden="true" />
                {strings.viewHistory}
              </button>
            ) : null}
            {viewData?.lead_id ? (
              <button
                type="button"
                className="justo-detail-footer__btn justo-detail-footer__btn--solid"
                onClick={() =>
                  navigate(`/updatestatusfollow/${viewData.lead_id}`)
                }
              >
                {strings.statusUpdate}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewFollowUp;
